import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Filter as FilterIcon, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { ActivityFeed } from '@/components/portfolio/activity-feed';
import {
  PortfolioEngagementsTable,
  type PortfolioEngagementsTableRow,
} from '@/components/portfolio/engagements-table';
import { PortfolioKpiStrip, type PortfolioKpiTile } from '@/components/portfolio/kpi-tiles';
import { PortfolioTrendCard } from '@/components/portfolio/trend-card';
import { type TeamMember, deriveInitials } from '@/components/clients/team-avatars';
import { useClientsDetailedQuery, useClientsQuery } from '@/lib/api/clients';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import {
  useEngagementDraftsQuery,
  useDeleteEngagementDraftMutation,
} from '@/lib/api/engagement-drafts';
import { NewEngagementWizard } from '@/components/modals/new-engagement-wizard';
import {
  emptyWizardState,
  engagementDraftLabel,
  type NewEngagementWizardState,
} from '@/lib/wizard/new-engagement-state';
import {
  useActivityFeedQuery,
  useEngagementDetailsQuery,
  useEngagementTrendsQuery,
  usePortfolioQuery,
  type PortfolioEngagementDto,
} from '@/lib/api/portfolio';
import { useEngagementsListQuery } from '@/lib/api/engagements';
import { rollupColorToPostureStatus } from '@/lib/api/health-posture';
import { formatDateTime } from '@/lib/utils/format';

/**
 * Portfolio dashboard route (C20-130 — rewrites the prior T-0107 grid
 * layout to match `design_handoff_caliber_v2/prototype/screens-portfolio.js`
 * exactly).
 *
 * Visual contract: the prototype renders a 10-column engagement-list table
 * with five KPI tiles above, a filter tab strip on the table card, an
 * "As of" timestamp on the right of the strip, and a bottom row of two
 * cards (posture-mix trend left, activity rail right).
 *
 * Data sources — every visible element binds to a real backend read; no
 * mock data anywhere. Per C20-130 hard requirement, fields with no backend
 * source surface as explicit "Pending" states rather than seeded demo
 * numbers. See PR body wire-up matrix.
 *
 *   - Composite `GET /api/v1/portfolio` powers the row list, KPI counts,
 *     and "As of" timestamp.
 *   - Per-row `GET /api/v1/engagements/:id` fans out for phase_path,
 *     budget_rollup, AI proposal count.
 *   - `GET /api/v1/engagements` provides planned_end (Go-Live) per row
 *     (composite doesn't carry it yet — backend gap).
 *   - Per-row `GET /api/v1/engagements/:id/readiness/trend` fans out for
 *     the row sparklines.
 *   - Per-row `GET /api/v1/engagements/:id/contract/pulse/posture-history`
 *     fans out for the bottom trend card.
 *   - Per-row `GET /api/v1/engagements/:id/audit` fans out for the
 *     activity rail (existing T-0107 wiring).
 */
export const Route = createFileRoute('/')({
  component: PortfolioPage,
});

function PortfolioPage() {
  return (
    <RequireAuth>
      <PortfolioContent />
    </RequireAuth>
  );
}

type TabId = 'all' | 'mine' | 'risk' | 'gl90';

function PortfolioContent() {
  const portfolioQuery = usePortfolioQuery();
  const engagements = React.useMemo<ReadonlyArray<PortfolioEngagementDto>>(
    () => portfolioQuery.data?.engagements ?? [],
    [portfolioQuery.data],
  );
  const engagementIds = React.useMemo(() => engagements.map((e) => e.id), [engagements]);

  // Per-row fan-outs. Each useQueries returns an array zipped by index
  // against `engagementIds`.
  const detailQueries = useEngagementDetailsQuery(engagementIds);
  const trendQueries = useEngagementTrendsQuery(engagementIds);

  // Engagement list — provides `planned_end` (Go-Live) which the composite
  // doesn't yet carry. Caller-scoped, so "My partner book" intersects
  // against it.
  const engagementsListQuery = useEngagementsListQuery();
  const engagementListById = React.useMemo(() => {
    const m = new Map<string, { planned_end: string | null; start_date: string | null }>();
    for (const e of engagementsListQuery.data?.engagements ?? []) {
      m.set(e.id, {
        planned_end: e.planned_end ?? null,
        start_date: e.start_date ?? null,
      });
    }
    return m;
  }, [engagementsListQuery.data]);

  // Activity rail — project an EngagementDto-shaped row for the existing
  // audit fan-out hook.
  const activity = useActivityFeedQuery(
    engagements.map((e) => ({
      id: e.id,
      project_id: e.project_id,
      project_name: e.name,
      client_id: e.client_id,
      client_name: e.client_name,
      name: e.name,
      status: e.status,
      modules_enabled: e.modules_enabled,
      banner_image_url: e.banner_image_url,
      created_at: e.created_at,
    })),
  );

  // Tier 3 — real Team column. Portfolio rows carry client_id; the client
  // detail carries team_member_user_ids (the portfolio team IS the client
  // team — there is no per-engagement roster in v1). Same fan-out +
  // batched users lookup as routes/clients/index.tsx.
  const distinctClientIds = React.useMemo(
    () => Array.from(new Set(engagements.map((e) => e.client_id))),
    [engagements],
  );
  const clientDetails = useClientsDetailedQuery(distinctClientIds);
  const teamIdsByClient = React.useMemo(() => {
    const m = new Map<string, ReadonlyArray<string>>();
    clientDetails.forEach((q, idx) => {
      const id = distinctClientIds[idx];
      if (id && q.data) m.set(id, q.data.team_member_user_ids ?? []);
    });
    return m;
  }, [clientDetails, distinctClientIds]);
  const allTeamIds = React.useMemo(() => {
    const set = new Set<string>();
    for (const ids of teamIdsByClient.values()) for (const uid of ids) set.add(uid);
    return Array.from(set);
  }, [teamIdsByClient]);
  const userLookup = useUsersLookupQuery(allTeamIds);
  const teamByClientId = React.useMemo(() => {
    const lookupById = new Map(
      (userLookup.data?.items ?? []).map((u) => [
        u.id,
        { display_name: u.display_name, avatar_color_index: u.avatar_color_index },
      ]),
    );
    const m = new Map<string, TeamMember[]>();
    for (const [clientId, ids] of teamIdsByClient) {
      m.set(
        clientId,
        ids.map((uid) => {
          const resolved = lookupById.get(uid);
          return {
            id: uid,
            name: resolved?.display_name ?? 'Team member',
            initials: deriveInitials(resolved?.display_name ?? uid.slice(0, 2)),
            colorIndex: resolved?.avatar_color_index ?? 0,
          };
        }),
      );
    }
    return m;
  }, [teamIdsByClient, userLookup.data]);

  // Tier 3 — resumable engagement drafts.
  const draftsQuery = useEngagementDraftsQuery();
  const deleteDraftMutation = useDeleteEngagementDraftMutation();
  const [resumeTarget, setResumeTarget] = React.useState<{
    id: string;
    state: NewEngagementWizardState;
  } | null>(null);

  const [activeTab, setActiveTab] = React.useState<TabId>('all');

  // "My partner book" — intersect with the caller-scoped engagements list.
  // Until a partner-only filter ships, we use the list as the lower bound.
  const callerEngagementIds = React.useMemo(() => {
    const set = new Set<string>();
    for (const e of engagementsListQuery.data?.engagements ?? []) set.add(e.id);
    return set;
  }, [engagementsListQuery.data]);

  const now = Date.now();

  const enrichedRows = React.useMemo<PortfolioEngagementsTableRow[]>(() => {
    return engagements.map((row, idx) => {
      const detailQ = detailQueries[idx];
      const trendQ = trendQueries[idx];
      const listRow = engagementListById.get(row.id);
      return {
        row,
        detail: detailQ?.data,
        detailLoading: detailQ?.isLoading ?? false,
        trend: trendQ?.data,
        trendLoading: trendQ?.isLoading ?? false,
        team: teamByClientId.get(row.client_id) ?? ([] as ReadonlyArray<TeamMember>),
        goLive: listRow?.planned_end ?? null,
      };
    });
  }, [engagements, detailQueries, trendQueries, engagementListById, teamByClientId]);

  const filteredRows = React.useMemo(() => {
    return enrichedRows.filter(({ row, goLive }) => {
      const postureStatus = rollupColorToPostureStatus(row.posture);
      if (activeTab === 'mine') return callerEngagementIds.has(row.id);
      if (activeTab === 'risk') return postureStatus === 'at-risk' || postureStatus === 'critical';
      if (activeTab === 'gl90') {
        if (!goLive) return false;
        const days = (new Date(goLive).getTime() - now) / 86_400_000;
        return days <= 90 && days >= 0;
      }
      return true;
    });
  }, [enrichedRows, activeTab, callerEngagementIds, now]);

  const summary = portfolioQuery.data?.summary;
  const postureCounts = summary?.posture_counts;
  const detailsReady = detailQueries.length > 0 && detailQueries.every((q) => !q.isLoading);

  const counts = React.useMemo(() => {
    let redFlagTotal = 0;
    let proposalsTotal = 0;
    let goLiveSoonTotal = 0;
    for (let i = 0; i < engagements.length; i++) {
      const row = engagements[i]!;
      redFlagTotal += row.finding_counts_by_severity.critical + row.finding_counts_by_severity.high;
      const detail = detailQueries[i]?.data;
      if (detail) {
        proposalsTotal += detail.recent_signals.filter((s) => s.kind === 'proposal').length;
      }
      const goLive = engagementListById.get(row.id)?.planned_end ?? null;
      if (goLive) {
        const days = (new Date(goLive).getTime() - now) / 86_400_000;
        if (days >= 0 && days <= 90) goLiveSoonTotal += 1;
      }
    }
    return { redFlagTotal, proposalsTotal, goLiveSoonTotal };
  }, [engagements, detailQueries, engagementListById, now]);

  const lastSync = React.useMemo(() => {
    let max: string | null = null;
    for (const e of engagements) {
      if (e.last_activity_at && (!max || e.last_activity_at > max)) max = e.last_activity_at;
    }
    return max ?? portfolioQuery.data?.computed_at ?? null;
  }, [engagements, portfolioQuery.data]);

  const callerEngCount = callerEngagementIds.size;
  const riskCount = engagements.filter((e) => {
    const s = rollupColorToPostureStatus(e.posture);
    return s === 'at-risk' || s === 'critical';
  }).length;

  const tabs = [
    { id: 'all', label: 'All engagements', count: engagements.length },
    { id: 'mine', label: 'My partner book', count: callerEngCount },
    {
      id: 'risk',
      label: 'At risk or critical',
      count: riskCount,
      accent: 'critical' as const,
    },
    {
      id: 'gl90',
      label: 'Go-Live <90 days',
      count: counts.goLiveSoonTotal,
    },
  ];

  if (portfolioQuery.isLoading) {
    return (
      <div className="p-card-pad">
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading your portfolio…
        </div>
      </div>
    );
  }

  if (portfolioQuery.error) {
    return (
      <div className="p-card-pad">
        <ErrorMessage error={portfolioQuery.error} />
      </div>
    );
  }

  if (engagements.length === 0) {
    return (
      <div className="space-y-6 p-card-pad">
        <PortfolioHeader engagementCount={0} lastSync={null} />
        <EmptyState
          title="No engagements yet"
          description="You don't have access to any engagements. Talk to your engagement lead."
        />
      </div>
    );
  }

  const kpiTiles: PortfolioKpiTile[] = [
    {
      label: 'Engagements',
      value: summary?.engagement_count ?? engagements.length,
      trend: postureCounts ? (
        <>
          <span style={{ color: 'var(--posture-healthy)' }}>{postureCounts.green} on track</span>
          <span aria-hidden="true">·</span>
          <span style={{ color: 'var(--posture-at-risk)' }}>
            {postureCounts.yellow_divergent} at risk
          </span>
          <span aria-hidden="true">·</span>
          <span style={{ color: 'var(--posture-critical)' }}>{postureCounts.red} critical</span>
        </>
      ) : (
        'Posture counts pending'
      ),
    },
    {
      label: 'Open red-flag risks',
      value: counts.redFlagTotal,
      trend:
        counts.redFlagTotal > 0
          ? `across ${
              engagements.filter(
                (e) =>
                  e.finding_counts_by_severity.critical + e.finding_counts_by_severity.high > 0,
              ).length
            } engagements`
          : 'No red-flag findings open',
    },
    {
      label: 'Overdue deliverables',
      value: summary?.overdue_deliverable_total ?? 0,
      trend: 'across the portfolio',
    },
    {
      label: 'Pending AI proposals',
      value: detailsReady ? counts.proposalsTotal : null,
      pending: !detailsReady,
      trend: detailsReady
        ? counts.proposalsTotal > 0
          ? 'awaiting consultant review'
          : 'no proposals queued'
        : 'Pending — loading engagement details',
    },
    {
      label: 'Go-Live decisions <90d',
      value: counts.goLiveSoonTotal,
      trend:
        counts.goLiveSoonTotal === 0
          ? 'No engagements within 90d window'
          : `${counts.goLiveSoonTotal} engagement${counts.goLiveSoonTotal === 1 ? '' : 's'} approaching`,
    },
  ];

  return (
    <div className="space-y-6 p-card-pad">
      <PortfolioHeader engagementCount={engagements.length} lastSync={lastSync} />

      <DraftsBanner
        drafts={draftsQuery.data?.drafts ?? []}
        onResume={(id, state) => setResumeTarget({ id, state })}
        onDiscard={(id) => deleteDraftMutation.mutate({ draftId: id })}
        discarding={deleteDraftMutation.isPending}
      />

      <PortfolioKpiStrip tiles={kpiTiles} />

      <PortfolioEngagementsTable
        rows={filteredRows}
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        asOf={lastSync}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
        <PortfolioTrendCard engagementIds={engagementIds} />
        <div className="lg:w-[380px]">
          <ActivityFeed
            items={activity.items}
            isLoading={activity.isLoading}
            isError={activity.isError}
            error={activity.error}
          />
        </div>
      </div>

      {resumeTarget ? (
        <NewEngagementWizard
          open
          resumeDraft={resumeTarget}
          onOpenChange={(next) => {
            if (!next) setResumeTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

function PortfolioHeader({
  engagementCount,
  lastSync,
}: {
  engagementCount: number;
  lastSync: string | null;
}) {
  const lastSyncLabel = lastSync ? formatTime(lastSync) : 'pending';
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-display font-semibold text-ink-900">Portfolio</h1>
        <p className="mt-1 text-meta text-ink-500">
          {engagementCount === 0
            ? 'You have no accessible engagements yet.'
            : `${engagementCount} engagement${engagementCount === 1 ? '' : 's'} · last sync ${lastSyncLabel}`}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {/*
          Filter — opens a multi-select filter sheet in a future ticket.
          Visible affordance per the prototype; the sheet is not wired in
          v1 (the prior multi-chip filter strip was retired with the
          C20-130 layout rewrite). Backend gap captured in PR body.
        */}
        {/*
          The prototype's "Export view" is left out until the portfolio can
          be exported: a disabled button whose tooltip said "pending backend
          CSV endpoint" was developer copy (C2-32).
        */}
        <Button
          type="button"
          data-create="engagement"
          size="sm"
          className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New engagement
        </Button>
      </div>
    </header>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Tier 3 — resumable engagement drafts. The wizard autosaves draft_state
 * every 500ms while dirty; this banner is the read side. Resume seeds the
 * wizard from the saved state and keeps writing the same draft row;
 * Discard deletes it. A draft with no engagement name yet goes by its
 * client; the names come from the firm's client list, which the sidebar
 * already reads (same query, so no extra request).
 */
function DraftsBanner({
  drafts,
  onResume,
  onDiscard,
  discarding,
}: {
  drafts: ReadonlyArray<{ id: string; draft_state?: unknown; updated_at: string }>;
  onResume: (id: string, state: NewEngagementWizardState) => void;
  onDiscard: (id: string) => void;
  discarding: boolean;
}) {
  const clients = useClientsQuery();
  const clientNames = React.useMemo(
    () => new Map((clients.data ?? []).map((c) => [c.id, c.name])),
    [clients.data],
  );
  if (drafts.length === 0) return null;
  return (
    <div className="rounded-card border border-accent-amber-100 bg-accent-amber-50 p-4">
      <p className="text-body-sm font-semibold text-ink-900">
        {drafts.length} unfinished engagement draft{drafts.length === 1 ? '' : 's'}
      </p>
      <ul className="mt-2 space-y-1.5">
        {drafts.map((d) => {
          const raw =
            d.draft_state && typeof d.draft_state === 'object'
              ? (d.draft_state as Partial<NewEngagementWizardState>)
              : {};
          const label = engagementDraftLabel(raw, clientNames);
          return (
            <li
              key={d.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-card bg-paper-0 px-3 py-2"
            >
              <span className="min-w-0 truncate text-body-sm text-ink-900">
                {label}
                <span className="ml-2 text-meta text-ink-500">
                  saved {formatDateTime(d.updated_at)}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    onResume(d.id, { ...emptyWizardState(), ...raw } as NewEngagementWizardState)
                  }
                >
                  Resume
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={discarding}
                  onClick={() => onDiscard(d.id)}
                >
                  Discard
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
